import { useState, useEffect } from "react";
import api from "../lib/api";
import { useWorkspace } from "../lib/workspaceContext";
import { Brain, ShieldAlert, Repeat, Sparkles, Loader2, AlertTriangle, CheckCircle2, TrendingUp, FileText } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "notes", label: "AI Auto Notes", icon: FileText },
  { id: "shadowban", label: "Shadowban Detector", icon: ShieldAlert },
  { id: "replay", label: "Viral Replay", icon: Repeat },
];

export default function IntelligenceHub() {
  const [tab, setTab] = useState("notes");
  return (
    <div className="space-y-8 animate-fadeUp" data-testid="intelligence-hub-page">
      <header className="border-l-2 border-[#CCFF00] pl-5">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-2">// INTELLIGENCE HUB</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">Cerveau analytique</h1>
        <p className="text-zinc-400 mt-2 max-w-2xl">3 systèmes IA branchés sur tes données : rapport quotidien automatique, détection de shadowban, replay des patterns gagnants.</p>
      </header>

      <div className="border-b border-[#1E1E24] flex gap-0 overflow-x-auto scrollbar-thin">
        {TABS.map(t => {
          const I = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`ih-tab-${t.id}`}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-mono uppercase tracking-wider transition border-b-2 whitespace-nowrap ${
                tab === t.id ? "text-[#CCFF00] border-[#CCFF00]" : "text-zinc-500 border-transparent hover:text-zinc-300"
              }`}>
              <I className="w-4 h-4" strokeWidth={1.5} />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "notes" && <AutoNotes />}
      {tab === "shadowban" && <ShadowbanDetector />}
      {tab === "replay" && <ViralReplay />}
    </div>
  );
}

// ============== AI AUTO NOTES ==============
function AutoNotes() {
  const { current } = useWorkspace();
  const [report, setReport] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true); setReport("");
    try {
      const [accounts, kpis, missions, goals] = await Promise.all([
        api.listAccounts(), api.listKpis(), api.listMissions(), api.listGoals()
      ]);
      const today = new Date().toISOString().slice(0, 10);
      const todayMissions = missions.filter(m => m.date === today);
      const completedMissions = todayMissions.filter(m => m.completed).length;

      const totalReach = kpis.reduce((s, k) => s + k.reach, 0);
      const avgEng = kpis.length ? (kpis.reduce((s, k) => s + k.engagement_rate, 0) / kpis.length).toFixed(2) : 0;
      const avgWt = kpis.length ? (kpis.reduce((s, k) => s + k.watchtime_pct, 0) / kpis.length).toFixed(1) : 0;
      const newFol = kpis.reduce((s, k) => s + k.new_followers, 0);

      const summary = [
        `Comptes actifs: ${accounts.length}`,
        `Reach total 14j: ${totalReach.toLocaleString("fr-FR")}`,
        `Engagement moyen: ${avgEng}%`,
        `Watchtime moyen: ${avgWt}%`,
        `Nouveaux abonnés 14j: ${newFol}`,
        `Missions du jour: ${completedMissions}/${todayMissions.length} faites`,
        `Objectifs actifs: ${goals.filter(g => g.status === "active").length}`,
        `Comptes en warmup/shadowban: ${accounts.filter(a => a.warmup_status === "warmup" || a.warmup_status === "shadowban").length}`,
      ].join(" · ");

      const r = await api.aiAutoNotes({ summary, workspace_name: current?.name || "Principal" });
      setReport(r.report || "");
    } catch { toast.error("Erreur IA"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-zinc-400 max-w-2xl">Claude analyse tes KPIs, missions, objectifs et produit un rapport stratégique structuré : performances · erreurs · actions · priorités demain.</p>
        <button onClick={generate} disabled={loading} data-testid="generate-notes-btn"
          className="bg-[#CCFF00] text-black font-medium px-5 py-2.5 rounded-sm hover:bg-[#B3E600] flex items-center gap-2 text-sm glow-volt">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {loading ? "Analyse..." : "Générer le rapport"}
        </button>
      </div>

      {report ? (
        <div className="border border-[#CCFF00]/30 bg-[#CCFF00]/[0.03] rounded-sm p-6">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#CCFF00] mb-3">// RAPPORT AUTOMATIQUE</div>
          <div className="text-zinc-200 text-sm leading-relaxed whitespace-pre-line prose-content" data-testid="auto-notes-content">{report}</div>
        </div>
      ) : (
        <div className="border border-dashed border-[#1E1E24] rounded-sm p-12 text-center">
          <FileText className="w-10 h-10 text-zinc-700 mx-auto mb-3" strokeWidth={1.2} />
          <div className="text-zinc-500 text-sm">Clique "Générer" pour obtenir ton rapport quotidien IA.</div>
        </div>
      )}
    </div>
  );
}

// ============== SHADOWBAN DETECTOR ==============
function ShadowbanDetector() {
  const [accounts, setAccounts] = useState([]);
  const [kpis, setKpis] = useState([]);

  useEffect(() => {
    (async () => {
      const [a, k] = await Promise.all([api.listAccounts(), api.listKpis()]);
      setAccounts(a); setKpis(k);
    })();
  }, []);

  const detect = (acc) => {
    const accKpis = kpis.filter(k => k.account_id === acc.id).sort((a, b) => a.date.localeCompare(b.date));
    if (accKpis.length < 5) return { risk: 0, level: "low", reasons: [] };

    const reasons = [];
    let risk = 0;

    // Drop reach >40% sur 3 derniers vs 3 précédents
    const last3 = accKpis.slice(-3);
    const prev3 = accKpis.slice(-6, -3);
    const recentReach = last3.reduce((s, k) => s + k.reach, 0) / 3;
    const prevReach = prev3.length ? prev3.reduce((s, k) => s + k.reach, 0) / 3 : recentReach;
    if (prevReach && (prevReach - recentReach) / prevReach > 0.4) {
      const drop = Math.round(((prevReach - recentReach) / prevReach) * 100);
      reasons.push({ severity: "high", text: `Chute reach -${drop}% sur 3 jours` });
      risk += 35;
    }

    // Watchtime < 30%
    const recentWt = last3.reduce((s, k) => s + k.watchtime_pct, 0) / 3;
    if (recentWt < 30) {
      reasons.push({ severity: "medium", text: `Watchtime ${recentWt.toFixed(1)}% < 30% — signal anti-spam possible` });
      risk += 20;
    }

    // Engagement effondré
    const recentEng = last3.reduce((s, k) => s + k.engagement_rate, 0) / 3;
    const prevEng = prev3.length ? prev3.reduce((s, k) => s + k.engagement_rate, 0) / 3 : recentEng;
    if (prevEng && (prevEng - recentEng) / prevEng > 0.5) {
      reasons.push({ severity: "high", text: `Engagement -${Math.round(((prevEng - recentEng) / prevEng) * 100)}% — suspect` });
      risk += 25;
    }

    // Status warmup
    if (acc.warmup_status === "shadowban") {
      reasons.push({ severity: "critical", text: "Statut compte = shadowban (manuel)" });
      risk += 50;
    }

    // Quotas trop élevés
    if (acc.daily_quota_likes > 200 || acc.daily_quota_follows > 50) {
      reasons.push({ severity: "medium", text: "Quotas excessifs (likes>200/j ou follows>50/j)" });
      risk += 15;
    }

    risk = Math.min(100, risk);
    const level = risk >= 70 ? "critical" : risk >= 40 ? "high" : risk >= 20 ? "medium" : "low";
    return { risk, level, reasons };
  };

  const COLOR = { low: "#10B981", medium: "#F59E0B", high: "#EF4444", critical: "#EF4444" };
  const LABEL = { low: "Sain", medium: "À surveiller", high: "Risque élevé", critical: "Action immédiate" };

  return (
    <div className="space-y-5">
      <div className="border border-[#CCFF00]/20 bg-[#CCFF00]/[0.02] rounded-sm p-4 text-sm text-zinc-300">
        <span className="text-[#CCFF00] font-medium">Détection rule-based</span> sur 5 facteurs : chute reach, watchtime faible, engagement effondré, statut warmup, quotas excessifs.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {accounts.map(acc => {
          const d = detect(acc);
          return (
            <div key={acc.id} className="border bg-[#0A0A0C] rounded-sm p-5 space-y-4" style={{ borderColor: `${COLOR[d.level]}40` }} data-testid={`shadow-${acc.label}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-white font-medium">{acc.label}</div>
                  <div className="text-[10px] font-mono text-zinc-500 mt-1">{acc.handle}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-3xl font-bold" style={{ color: COLOR[d.level] }}>{d.risk}</div>
                  <div className="text-[10px] font-mono uppercase tracking-wider mt-1" style={{ color: COLOR[d.level] }}>{LABEL[d.level]}</div>
                </div>
              </div>

              <div className="h-2 bg-[#1E1E24] rounded-full overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${d.risk}%`, background: COLOR[d.level] }} />
              </div>

              {d.reasons.length === 0 ? (
                <div className="flex items-center gap-2 text-sm text-[#10B981]">
                  <CheckCircle2 className="w-4 h-4" />
                  Aucune anomalie détectée
                </div>
              ) : (
                <ul className="space-y-2">
                  {d.reasons.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: r.severity === "critical" || r.severity === "high" ? "#EF4444" : "#F59E0B" }} />
                      <span>{r.text}</span>
                    </li>
                  ))}
                </ul>
              )}

              {d.risk >= 40 && (
                <div className="border-t border-[#1E1E24] pt-3 text-xs text-zinc-400 leading-relaxed">
                  <span className="text-[#EF4444] font-medium">Action :</span> stop posts 7-14j · désactive compte 3j · reprends en warm-up doux 21 jours minimum.
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============== VIRAL REPLAY ==============
function ViralReplay() {
  const [posts, setPosts] = useState([]);
  const [hooks, setHooks] = useState([]);
  const [accounts, setAccounts] = useState([]);

  useEffect(() => {
    (async () => {
      const [p, h, a] = await Promise.all([api.listPosts(), api.listHooks(), api.listAccounts()]);
      setPosts(p); setHooks(h); setAccounts(a);
    })();
  }, []);

  // Hooks scoring distribution
  const topHooks = [...hooks].sort((a, b) => b.score - a.score).slice(0, 5);
  const avgHookScore = hooks.length ? Math.round(hooks.reduce((s, h) => s + h.score, 0) / hooks.length) : 0;

  // Patterns par catégorie
  const catCounts = {};
  hooks.forEach(h => { catCounts[h.category] = (catCounts[h.category] || 0) + 1; });
  const topCat = Object.entries(catCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "—";

  // Posts par format
  const formatCounts = {};
  posts.forEach(p => { formatCounts[p.format] = (formatCounts[p.format] || 0) + 1; });
  const topFormat = Object.entries(formatCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "reel";

  // Plateforme dominante
  const platCounts = {};
  posts.forEach(p => { platCounts[p.platform] = (platCounts[p.platform] || 0) + 1; });
  const topPlat = Object.entries(platCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "instagram";

  return (
    <div className="space-y-6">
      <div className="border border-[#CCFF00]/20 bg-[#CCFF00]/[0.02] rounded-sm p-4 text-sm text-zinc-300">
        <span className="text-[#CCFF00] font-medium">Le replay</span> identifie les patterns gagnants dans tes hooks et posts existants → te suggère quoi refaire en priorité.
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Score hook moyen" value={avgHookScore} suffix="/100" color="#CCFF00" />
        <Stat label="Catégorie dominante" value={topCat} color="#00D4FF" />
        <Stat label="Format gagnant" value={topFormat} color="#10B981" />
        <Stat label="Plateforme dominante" value={topPlat} color="#A78BFA" />
      </div>

      <section>
        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-3">// TOP 5 HOOKS À RÉPLIQUER</div>
        {topHooks.length === 0 ? (
          <div className="border border-dashed border-[#1E1E24] rounded-sm p-8 text-center text-zinc-500 text-sm">
            Aucun hook scoré. Ajoute des hooks dans la Banque pour activer le replay.
          </div>
        ) : (
          <div className="space-y-2">
            {topHooks.map((h, i) => (
              <div key={h.id} className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-4 flex items-center gap-4 hover:border-white/20" data-testid={`replay-hook-${i}`}>
                <div className="font-mono text-2xl font-medium text-[#CCFF00] w-12 text-center">#{String(i + 1).padStart(2, "0")}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm font-medium">{h.text}</div>
                  <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider mt-1">
                    {h.category} · {h.platform}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-lg text-[#CCFF00]">{h.score}</div>
                  <div className="text-[9px] font-mono uppercase text-zinc-500">score</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="border border-[#CCFF00]/30 bg-[#CCFF00]/[0.03] rounded-sm p-5">
        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#CCFF00] mb-2">// RECOMMANDATIONS REPLAY</div>
        <ul className="space-y-2 text-sm text-zinc-200">
          <li className="flex items-start gap-2"><TrendingUp className="w-4 h-4 text-[#CCFF00] shrink-0 mt-0.5" strokeWidth={1.5} /> Refais ce type de hook : <span className="text-[#CCFF00] font-medium">"{topCat}"</span> — c'est ta catégorie gagnante.</li>
          <li className="flex items-start gap-2"><TrendingUp className="w-4 h-4 text-[#CCFF00] shrink-0 mt-0.5" strokeWidth={1.5} /> Format à privilégier : <span className="text-[#CCFF00] font-medium">{topFormat}</span> — déjà dominant dans ton planning.</li>
          <li className="flex items-start gap-2"><TrendingUp className="w-4 h-4 text-[#CCFF00] shrink-0 mt-0.5" strokeWidth={1.5} /> Capitalise sur <span className="text-[#CCFF00] font-medium">{topPlat}</span> avant de scaler vers d'autres plateformes.</li>
          <li className="flex items-start gap-2"><TrendingUp className="w-4 h-4 text-[#CCFF00] shrink-0 mt-0.5" strokeWidth={1.5} /> Re-publie une variation de ton hook #1 dans 7-14 jours pour un "second wave" effect.</li>
        </ul>
      </div>
    </div>
  );
}

const Stat = ({ label, value, suffix, color }) => (
  <div className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-4">
    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-2">{label}</div>
    <div className="font-mono text-2xl font-medium" style={{ color }}>{value}{suffix && <span className="ml-1 text-zinc-500 text-sm">{suffix}</span>}</div>
  </div>
);
